import { type FC, useEffect, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Button } from "components/buttons/button/Button";
import { useObserver } from "hooks/useObserver";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import ModalWithSteps from "components/modal/ModalWithSteps";
import LicensesAddingStep from "features/Accounts/PackAssignmentModal/packAssignmentSteps/LicensesStep/LicensesAddingStep";
import { type ExtendLicensesModalProps } from "features/Accounts/PackAssignmentModal/extendLicensesModal/types";
import {
  accountPackLicenseSelector,
  reset,
} from "features/Accounts/EditAccount/state/slices/editAccountPackLicenseSlice";
import { fetchAccountPackLicense } from "features/Accounts/EditAccount/state/thunks/editAccountPackLicenseThunk";
import { type AccountAvailablePackOverview, type AssignAccountPack } from "features/Accounts/PackAssignmentModal/types";
import { MAX_LICENSE_COUNT } from "features/Accounts/PackAssignmentModal/constants";

import "features/Accounts/PackAssignmentModal/extendLicensesModal/extendLicensesModal.scss";
import { PricingTypes } from "enums/licensing/pricingTypes";

type PricingFlags = {
  isFree: boolean;
  isOther: boolean;
};

type LicenseMetadata = {
  unlimitedLicenses?: boolean;
  noExpiration?: boolean;
  packPricingTypeId?: number;
};

const getPricingFlags = (packPricingTypeId?: number): PricingFlags => ({
  isFree: packPricingTypeId === PricingTypes.Free,
  isOther: packPricingTypeId === PricingTypes.Other,
});

const shouldApplyUnlimitedLicenses = (license: LicenseMetadata, flags: PricingFlags, isRestore: boolean): boolean => {
  return Boolean(isRestore && license.unlimitedLicenses && (flags.isFree || flags.isOther));
};

const shouldApplyNoExpiration = (license: LicenseMetadata, flags: PricingFlags, isRestore: boolean): boolean => {
  if (!isRestore) {
    return false;
  }

  if (flags.isFree) {
    return true;
  }

  return Boolean(flags.isOther && license.noExpiration);
};

export type ExtendLicensesModalAllProps = PropsFromRedux & ExtendLicensesModalProps;

export const ExtendLicensesModal: FC<ExtendLicensesModalAllProps> = ({
  onConfirm,
  selectedViewType,
  showModalObserver,
  accountPackLicense,
  fetchAccountPackLicense,
  resetAccountPackLicense,
  isLoading,
  error,
  header,
  isRestore,
  selectedAccountId,
  packPricingTypeId,
}) => {
  const [isDataValid, setIsDataValid] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [packId, setPackId] = useState(0);
  const [selectedPack, setSelectedPack] = useState<AssignAccountPack>();
  const [extendLicensesPackItem, setExtendLicensesPackItem] = useState<AccountAvailablePackOverview>();

  useEffect(() => {
    setIsDataValid(false);

    if (!accountPackLicense) {
      setSelectedPack(undefined);
      setExtendLicensesPackItem(undefined);
      return;
    }

    const flags = getPricingFlags(packPricingTypeId);
    const restoreMode = Boolean(isRestore);
    const licenseMetadata: LicenseMetadata = {
      packPricingTypeId,
      unlimitedLicenses: accountPackLicense.unlimitedLicenses,
      noExpiration: accountPackLicense.noExpiration,
    };
    const noExpiration =
      shouldApplyNoExpiration(licenseMetadata, flags, restoreMode) || Boolean(accountPackLicense.noExpiration);
    const unlimitedLicenses =
      shouldApplyUnlimitedLicenses(licenseMetadata, flags, restoreMode) ||
      Boolean(accountPackLicense.unlimitedLicenses);

    const nextSelectedPack: AssignAccountPack = {
      packId,
      licensesCount: accountPackLicense.totalLicensesCount,
      assignedLicensesCount: accountPackLicense.assingedLicensesCount,
      isRequested: false,
      edited: false,
      licenseType: accountPackLicense.licenseTypeId,
    };

    const packItem = {
      id: packId,
      title: accountPackLicense.packTitle,
      thumbnailImageUrl: accountPackLicense.thumbnailImageUrl,
      packPricingTypeId,
      noExpiration,
      unlimitedLicenses,
    } as AccountAvailablePackOverview;

    setExtendLicensesPackItem(packItem);
    setSelectedPack(nextSelectedPack);
  }, [showModal, packId, accountPackLicense, isRestore, packPricingTypeId]);

  /* istanbul ignore next */
  const onExtendeLicensesButtonClick = (packId: number, accountId: number) => {
    fetchAccountPackLicense(packId, accountId);
    setPackId(packId);
    setShowModal(true);
  };
  const [subscribeOnExtendeLicensesButtonClick] = useObserver(showModalObserver);

  useEffect(() => {
    return subscribeOnExtendeLicensesButtonClick(onExtendeLicensesButtonClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeOnExtendeLicensesButtonClick]);

  const onCloseModal = () => {
    setShowModal(false);
    resetAccountPackLicense();
  };

  const onLicenseCountChange = (licensesCount?: number) => {
    if (selectedPack) {
      selectedPack.licensesCount = licensesCount;
      selectedPack.edited = true;
      const updatedPack = { ...selectedPack };
      setSelectedPack(updatedPack);
    }
  };

  const validateFinishBtnEnabling = (maxLicensesCount: number) => {
    if (!accountPackLicense) {
      setIsDataValid(false);
      return;
    }

    const licensesCount = selectedPack?.licensesCount || 0;
    const flags = getPricingFlags(packPricingTypeId);
    const licenseMetadata: LicenseMetadata = {
      packPricingTypeId,
      unlimitedLicenses: accountPackLicense.unlimitedLicenses,
      noExpiration: accountPackLicense.noExpiration,
    };
    const restoreMode = Boolean(isRestore);
    const hasUnlimitedLicenses =
      shouldApplyUnlimitedLicenses(licenseMetadata, flags, restoreMode) ||
      Boolean(accountPackLicense.unlimitedLicenses);
    const hasNoExpiration =
      shouldApplyNoExpiration(licenseMetadata, flags, restoreMode) || Boolean(accountPackLicense.noExpiration);

    const assignedLicenses = selectedPack?.assignedLicensesCount ?? 0;
    const requiresLicenseCount =
      !hasUnlimitedLicenses &&
      (licensesCount > maxLicensesCount || (assignedLicenses > 0 && assignedLicenses > licensesCount));
    const requiresExpiration = !hasNoExpiration && !selectedPack?.expirationDate;

    const isInvalid = requiresLicenseCount || requiresExpiration || Boolean(error);

    setIsDataValid(!isInvalid);
  };

  useEffect(() => {
    if (accountPackLicense) {
      validateFinishBtnEnabling(MAX_LICENSE_COUNT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountPackLicense]);

  const onExpirationDateChange = (optionId?: number, date?: Date | null) => {
    if (selectedPack) {
      if (optionId) {
        selectedPack.licenseType = optionId;
      }
      if (date) {
        selectedPack.expirationDate = date;
      }

      const updatedPack = { ...selectedPack };
      setSelectedPack(updatedPack);
    }
  };

  const renderModalLicensesAddingStep = (_nextStep: () => void, _prevStep: () => void) => (closeModal: Function) => (
    <>
      <Button
        basic
        className="cancel"
        content="Cancel"
        onClick={() => {
          onCloseModal();
          closeModal();
        }}
      />

      <Button
        primary
        className="confirm"
        content="Save"
        onClick={() => {
          onConfirm(selectedPack ? [selectedPack] : [], selectedViewType);
          onCloseModal();
          closeModal();
        }}
        disabled={!isDataValid}
      />
    </>
  );

  return (
    <ModalWithSteps className={"extend-licenses-modal"} scrolling={false} showModal={showModal} onCancel={onCloseModal}>
      <LicensesAddingStep
        header={header}
        isLoading={isLoading}
        selectedPacks={selectedPack ? [selectedPack] : []}
        renderModalActions={renderModalLicensesAddingStep}
        items={extendLicensesPackItem ? [extendLicensesPackItem] : []}
        onLicenseCountChange={(_, licensesCount?: number) => onLicenseCountChange(licensesCount)}
        validateFinishBtnEnabling={validateFinishBtnEnabling}
        onExpirationDateChange={(_, optionId?: number, date?: Date | null) => onExpirationDateChange(optionId, date)}
        error={error}
        isRestore={isRestore}
        accountId={selectedAccountId}
      />
    </ModalWithSteps>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isLoading: accountPackLicenseSelector(state).isLoading,
  error: accountPackLicenseSelector(state).error,
  accountPackLicense: accountPackLicenseSelector(state).accountPackLicense,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetAccountPackLicense: () => dispatch(reset()),
    fetchAccountPackLicense: (packId: number, accountId: number) =>
      dispatch(fetchAccountPackLicense(packId, accountId)),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ExtendLicensesModal);
export default ConnectedComponent;
